import React from "react";
import {
  CertificateElement,
  ReportChartPreview,
  ReportChecklistPreview,
  ReportTable,
} from "./certificateTypes";
import { DEFAULT_CERTIFICATE_LOGO } from "./certificateTemplates";

type CertificateElementViewProps = {
  element: CertificateElement;
  selected: boolean;
  interactive: boolean;
  content: string;
  table?: ReportTable;
  chart?: ReportChartPreview;
  checklist?: ReportChecklistPreview;
  inheritedFontFamily: string;
  onSelect: (id: string) => void;
  onMoveStart: (event: React.PointerEvent, id: string) => void;
  onResizeStart: (event: React.PointerEvent, id: string) => void;
  logoSrc?: string;
};

type SurfaceFill = {
  first: string;
  secound: string;
  third: string;
  fourth: string;
  center: string;
};

const PETAL =
  "M 181.507 301.928 L 332.02 449.634 C 332.02 449.634 426.933 357.53 528.024 449.636 L 679.662 303.614 C 679.662 303.614 655.518 231.704 471.301 280.589 C 446.875 287.072 440.412 285.08 411.77 282.833 C 383.128 280.587 216.889 224.989 181.507 301.928 Z";

const SID_KEYS = ["LA", "ME", "LI", "DI", "OC"];
const SURFACE_FIELDS: Array<keyof SurfaceFill> = [
  "first",
  "secound",
  "third",
  "fourth",
  "center",
];

const REPORT_AMOUNT_HEADERS = new Set([
  "qty",
  "price",
  "total",
  "discount",
  "cash",
  "card",
  "amount",
  "paid",
  "balance",
]);

/** Bill tables keep their own column widths. Patient report tables do not. */
function isPatientReportTable(columns: string[]) {
  return (
    columns.map((label) => label.trim().toLowerCase()).join("|") !==
    "no|treatment|rate|units|price"
  );
}

const QUADS: number[][] = [
  [18, 17, 16, 15, 14, 13, 12, 11],
  [21, 22, 23, 24, 25, 26, 27, 28],
  [48, 47, 46, 45, 44, 43, 42, 41],
  [31, 32, 33, 34, 35, 36, 37, 38],
];

function whiteFill(): SurfaceFill {
  return {
    first: "white",
    secound: "white",
    third: "white",
    fourth: "white",
    center: "white",
  };
}

function surfacesFor(toothNo: number, chart?: ReportChartPreview): SurfaceFill {
  const fill = whiteFill();
  const actions = chart?.actions || [];
  const matches = actions.filter(
    (item) => String(item.toothNo) === String(toothNo) && item.isTop !== false
  );
  if (matches.length) {
    matches.forEach((item) => {
      if (item.hasSurface && item.surface) {
        const index = SID_KEYS.indexOf(item.surface);
        if (index >= 0) fill[SURFACE_FIELDS[index]] = item.color || "#ef4444";
      } else {
        const color = item.color || "#ef4444";
        SURFACE_FIELDS.forEach((key) => {
          fill[key] = color;
        });
      }
    });
    return fill;
  }
  if (chart?.marked?.some((n) => String(n) === String(toothNo))) {
    SURFACE_FIELDS.forEach((key) => {
      fill[key] = "#ef4444";
    });
  }
  return fill;
}

const PETAL_PLACEMENTS = [
  { dx: 6, dy: 1, rot: 0, key: "first" as const },
  { dx: 55, dy: 6.5, rot: 90, key: "secound" as const },
  { dx: 49, dy: 56, rot: 180, key: "third" as const },
  { dx: 0, dy: 50, rot: 270, key: "fourth" as const },
];

function ToothGlyph({ fill }: { fill: SurfaceFill }) {
  return (
    <svg
      className="report-odonto-glyph"
      viewBox="8.8 9.8 37 36.2"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      {PETAL_PLACEMENTS.map((petal) => (
        <g
          key={petal.key}
          transform={`translate(${petal.dx} ${petal.dy}) rotate(${petal.rot}) scale(0.05)`}
        >
          <path d={PETAL} fill={fill[petal.key]} stroke="#111827" strokeWidth="10" />
        </g>
      ))}
      <circle
        cx="27.5"
        cy="28.5"
        r="7"
        fill={fill.center}
        fillOpacity="0.85"
        stroke="#111827"
        strokeWidth="1"
      />
    </svg>
  );
}

function OdontoTooth({
  toothNo,
  fill,
}: {
  toothNo: number;
  fill: SurfaceFill;
}) {
  return (
      <div className="report-odonto-tooth">
      <span className="report-odonto-num">{toothNo}</span>
      <ToothGlyph fill={fill} />
    </div>
  );
}

function DiagnosisChartPreview({ chart }: { chart?: ReportChartPreview }) {
  return (
    <div className="report-odontogram">
      {QUADS.map((nums, index) => (
        <div key={index} className="report-odonto-quad">
          {nums.map((toothNo) => (
            <OdontoTooth
              key={toothNo}
              toothNo={toothNo}
              fill={surfacesFor(toothNo, chart)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function MedicalHistoryChecklist({
  items,
}: {
  items: ReportChecklistPreview["items"];
}) {
  const rows = items.length
    ? items
    : [{ label: "No medical history", checked: false }];
  return (
    <div className="report-history-grid">
      {rows.map((item) => (
        <div key={item.label} className="report-history-item">
          <span className={`report-history-box${item.checked ? " is-checked" : ""}`}>
            {item.checked ? (
              <svg viewBox="0 0 12 12" aria-hidden="true">
                <path
                  d="M2 6.2 L4.6 8.7 L10 3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
          </span>
          <span className="report-history-copy">
            <span className="report-history-label">{item.label}</span>
            {item.note ? <span className="report-history-note">{item.note}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function CertificateElementView({
  element,
  selected,
  interactive,
  content,
  table,
  chart,
  checklist,
  inheritedFontFamily,
  onSelect,
  onMoveStart,
  onResizeStart,
  logoSrc,
}: CertificateElementViewProps) {
  if (!element.visible) return null;

  return (
    <div
      className={`certificate-element${selected ? " is-selected" : ""}${
        element.type === "logo" ? " is-logo" : ""
      }${element.type === "table" || table ? " is-table" : ""}${
        element.type === "chart" || chart ? " is-chart" : ""
      }${element.type === "checklist" || checklist ? " is-checklist" : ""}${
        String(element.id).split("__")[0] === "reportDescription"
          ? " is-description-box"
          : ""
      }${element.textTransform === "uppercase" ? " is-uppercase" : ""}${
        element.textTransform === "capitalize" ? " is-capitalize" : ""
      }`}
      style={{
        left: `${element.x}mm`,
        top: `${element.y}mm`,
        width: `${element.width}mm`,
        height: `${element.height}mm`,
        fontSize: `${element.fontSize}pt`,
        fontWeight: element.type === "table" || table ? 400 : element.fontWeight,
        fontFamily: element.fontFamily || inheritedFontFamily,
        ...(element.italic !== undefined
          ? { fontStyle: element.italic ? "italic" : "normal" }
          : {}),
        ...(element.underline !== undefined
          ? { textDecoration: element.underline ? "underline" : "none" }
          : {}),
        // Tables control alignment per-cell; wrapper align shifts th/td differently in PDF
        textAlign: element.type === "table" || table ? "left" : element.align,
        lineHeight:
          element.type === "table" || table
            ? `${Math.max(12, Math.round(element.fontSize * 1.45))}px`
            : element.type === "chart" || chart
              ? "16px"
              : element.lineHeight,
        letterSpacing: element.letterSpacing ? `${element.letterSpacing}px` : undefined,
        color: element.color,
        backgroundColor:
          element.type === "table" || table
            ? "transparent"
            : element.backgroundColor,
        padding:
          element.type === "table" || table
            ? 0
            : element.backgroundColor && element.height > 4
              ? "1.5mm 2.5mm"
              : undefined,
        ["--report-cell-pad" as string]:
          element.paddingMm != null && Number.isFinite(element.paddingMm)
            ? `${element.paddingMm}mm`
            : undefined,
        ["--report-table-font-size" as string]:
          element.type === "table" || table
            ? `${element.fontSize}pt`
            : undefined,
        boxSizing: "border-box",
        cursor: interactive ? "move" : "default",
      }}
      onPointerDown={(event) => {
        if (!interactive) return;
        event.stopPropagation();
        onSelect(element.id);
        onMoveStart(event, element.id);
      }}
    >
      {element.type === "logo" ? (
        <img
          src={logoSrc || undefined}
          alt=""
          onError={(event) => {
            const img = event.currentTarget;
            if (img.dataset.fallback === "1") return;
            img.dataset.fallback = "1";
            img.src = DEFAULT_CERTIFICATE_LOGO;
          }}
        />
      ) : element.type === "chart" || chart ? (
        <DiagnosisChartPreview chart={chart} />
      ) : element.type === "checklist" || checklist?.items?.length ? (
        <MedicalHistoryChecklist items={checklist?.items || []} />
      ) : table?.columns?.length ? (
        <table
          className={`report-canvas-table${
            (table as ReportTable).hideHeader ? " is-no-header" : ""
          }${isPatientReportTable(table.columns) ? " is-report-grid" : ""}`}
        >
          {(table as ReportTable).hideHeader !== true ? (
            <thead>
              <tr>
                {table.columns.map((col) => (
                  <th
                    key={col}
                    className={
                      isPatientReportTable(table.columns) &&
                      REPORT_AMOUNT_HEADERS.has(col.trim().toLowerCase())
                        ? "is-amount"
                        : undefined
                    }
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {(table.rows.length ? table.rows : [["—"]]).map((row, index) => (
              <tr key={index}>
                {table.columns.map((col, colIndex) => (
                  <td
                    key={`${col}-${colIndex}`}
                    className={
                      isPatientReportTable(table.columns) &&
                      REPORT_AMOUNT_HEADERS.has(col.trim().toLowerCase())
                        ? "is-amount"
                        : undefined
                    }
                  >
                    {row[colIndex] ?? ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        content
      )}
      {interactive && selected ? (
        <span
          className="certificate-resize-handle"
          onPointerDown={(event) => {
            event.stopPropagation();
            onResizeStart(event, element.id);
          }}
        />
      ) : null}
    </div>
  );
}

export default CertificateElementView;
